import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import * as XLSX from 'xlsx';
import { DocumentTypeIconComponent } from './document-type-icon/document-type-icon.component';

// Stateless helpers shared by the "read" (preview) and "download" flows in
// SelectAttachmentsComponent, for both newly uploaded documents and collection documents.
@Injectable({ providedIn: 'root' })
export class AttachmentFileService {

  // Resolves a reachable URL for a file from a read-document API response. When the
  // response carries base64 content, an object URL is created and handed to `trackUrl`
  // so the caller can revoke it later (e.g. on component destroy).
  resolveFileUrl(source$: Observable<any>, name: string, trackUrl: (url: string) => void): Promise<string | null> {
    return new Promise<string | null>((resolve, reject) => {
      source$.subscribe({
        next: (res: any) => {
          const body = res?.body;
          if (res?.isBase64Encoded && body) {
            try {
              const byteArray = Uint8Array.from(atob(body), c => c.codePointAt(0) || 0);
              const blob = new Blob([byteArray], { type: DocumentTypeIconComponent.getMimeType(name) });
              const url = URL.createObjectURL(blob);
              trackUrl(url);
              resolve(url);
            } catch (e) {
              console.trace('Base64 conversion failed', e);
              resolve(null);
            }
          } else if (typeof body === 'string') {
            resolve(body);
          } else {
            resolve(null);
          }
        },
        error: (err) => reject(err)
      });
    });
  }

  async fetchDocxBuffer(file: any, getUrl: () => Promise<string | null>): Promise<ArrayBuffer | null> {
    if (file.file_name) {
      try {
        const url = await getUrl();
        if (url) {
          const resp = await fetch(url);
          if (resp.ok) { return await resp.arrayBuffer(); }
        }
      } catch (err) {
        console.trace('Failed to fetch docx from backend', err);
      }
    }
    return file.file ? file.file.arrayBuffer() : null;
  }

  async buildPreviewWorkbook(file: any, name: string, getUrl: () => Promise<string | null>): Promise<any> {
    if (name.endsWith('.csv')) {
      if (file.file && typeof file.file.text === 'function') {
        return XLSX.read(await file.file.text(), { type: 'string' });
      }
      const csvUrl = await getUrl();
      if (!csvUrl) { throw new Error('No file data available for preview'); }
      const csvResp = await fetch(csvUrl);
      if (!csvResp.ok) { throw new Error('Failed to fetch file for preview'); }
      return XLSX.read(await csvResp.text(), { type: 'string' });
    }
    if (file.file && typeof file.file.arrayBuffer === 'function') {
      return XLSX.read(await file.file.arrayBuffer(), { type: 'array' });
    }
    const xlsUrl = await getUrl();
    if (!xlsUrl) { throw new Error('No file data available for preview'); }
    const xlsResp = await fetch(xlsUrl);
    if (!xlsResp.ok) { throw new Error('Failed to fetch file for preview'); }
    return XLSX.read(await xlsResp.arrayBuffer(), { type: 'array' });
  }

  async openPdf(getUrl: () => Promise<string | null>, file: any, trackUrl: (url: string) => void): Promise<void> {
    const url = await getUrl();
    if (url) { globalThis.open(url, '_blank'); return; }
    if (file.file) {
      const obj = URL.createObjectURL(file.file);
      trackUrl(obj);
      globalThis.open(obj, '_blank');
      return;
    }
    alert('PDF preview requires a reachable URL; please download the file to view.');
  }

  // Converts a base64 read-document API response into a downloaded file.
  downloadFromResponse(res: any, fileName: string, defaultMimeType: string): void {
    if (!res?.body) {
      console.trace('No body returned from API');
      return;
    }
    try {
      const base64Data = res.body.includes(',') ? res.body.split(',')[1] : res.body;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.codePointAt(i) || 0;
      }

      const byteArray = new Uint8Array(byteNumbers);
      const contentType = res.headers?.['Content-Type'] || defaultMimeType;
      const blob = new Blob([byteArray], { type: contentType });

      const url = globalThis.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();

      globalThis.URL.revokeObjectURL(url);
    } catch (error) {
      console.trace('Download failed', error);
    }
  }
}
